import { useState, useEffect, useRef } from "react";
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  documentId,
  Timestamp,
} from "firebase/firestore";
import { db } from "./config";
import { validateTrackingId } from "./access-control";
import { ensureFirebaseAuthReady } from "./auth-ready";
import { env } from "../env";

interface FirebaseData {
  accessibleUserIds?: string[];
  [key: string]: unknown;
}

/**
 * Any Date-typed field the backend embeds when mirroring a row (e.g. an
 * EmailAccount's `createdAt` inside a bulk job/import's `items[]`) comes back
 * from Firestore as a `Timestamp` instance, not a plain string — the Admin
 * SDK auto-converts real JS `Date` values on write. React can't render a
 * `Timestamp` directly ("object with keys {seconds, nanoseconds}"), so
 * convert every one found anywhere in the document — including nested inside
 * arrays — to an ISO string before handing the data to any caller. Single
 * boundary: every place below that reads `snapshot.data()` routes through
 * this first.
 */
function convertTimestamps(value: unknown): unknown {
  if (value instanceof Timestamp) {
    return value.toDate().toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(convertTimestamps);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, v]) => [key, convertTimestamps(v)]),
    );
  }
  return value;
}

/**
 * The Firestore rules read `resource.data.accessibleUserIds`, so a deleted (or
 * never-created) doc is DENIED rather than reported absent: a single-doc
 * listener receives `permission-denied` where it used to get `exists() ===
 * false`. Treat that error as "doc gone" — for these collections, deletion is
 * how a job signals completion.
 */
function isGoneError(error: { code?: string }): boolean {
  return error.code === "permission-denied";
}

export const useFirebaseData = (
  uid?: string | number,
  seedTestId?: string,
  collectionName?: string,
  shouldStart?: boolean,
  changeFireBaseConnected?: (isActive: boolean) => void,
) => {
  const [data, setData] = useState<FirebaseData | null>(null);

  const [isConnectionPending, setIsConnectionPending] = useState(false);

  // Confirmed "doc is gone / inaccessible" — distinct from "haven't received
  // a snapshot yet" (which is just isConnectionPending). Callers use this to
  // detect an expired/deleted job record without relying on a REST 404.
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Don't fetch if no testId is provided
    if (!shouldStart || !seedTestId) {
      setData(null);
      setIsConnectionPending(false);
      setNotFound(false);
      return;
    }

    setNotFound(false);
    let unsubscribe: (() => void) | undefined;

    const validateAndFetch = async () => {
      try {
        setIsConnectionPending(true);

        // Sign in to Firebase as this user (or await the in-flight sign-in)
        // before any Firestore call — the rules scope reads to the uid. With
        // no session every read would be denied, which must not read as "the
        // job is gone", so stop here instead.
        const firebaseUid = await ensureFirebaseAuthReady();
        if (!firebaseUid) {
          setIsConnectionPending(false);
          return;
        }

        // Validate tracking ID first
        const hasAccess = await validateTrackingId(
          uid ?? "",
          collectionName ?? "",
          seedTestId,
        );

        if (!hasAccess) {
          setData(null);
          setIsConnectionPending(false);
          setNotFound(true);

          if (env.appEnv === "production") {
            // TODO: switch to a PostHog exception-capture helper once
            // lib/analytics.ts grows one.
            console.warn(
              "[firebase] access denied: invalid ID or document does not exist",
              { seedTestId },
            );
          }
          return;
        }

        // Now set up the Firebase listener
        unsubscribe = onSnapshot(
          doc(db, collectionName ?? "", seedTestId),
          (docSnapshot) => {
            if (docSnapshot.exists()) {
              changeFireBaseConnected?.(true);
              const newData = convertTimestamps({
                ...docSnapshot.data(),
              }) as FirebaseData;

              setData(newData);
              setIsConnectionPending(false);
              setNotFound(false);
            } else {
              setData(null);
              changeFireBaseConnected?.(false);
              setIsConnectionPending(false);
              setNotFound(true);

              if (env.appEnv === "production") {
                // TODO: switch to a PostHog exception-capture helper once
                // lib/analytics.ts grows one.
                console.warn("[firebase] document does not exist", {
                  seedTestId,
                  collection: collectionName,
                });
              }
            }
          },
          (error) => {
            setIsConnectionPending(false);
            if (isGoneError(error)) {
              setData(null);
              changeFireBaseConnected?.(false);
              setNotFound(true);
              return;
            }
            if (env.appEnv === "production") {
              // TODO: switch to a PostHog exception-capture helper once
              // lib/analytics.ts grows one.
              console.error("[firebase] firestore listener error:", error, {
                seedTestId,
                collection: collectionName,
                errorCode: error.code,
              });
            }
          },
        );
      } catch (error) {
        setIsConnectionPending(false);
        if (env.appEnv === "production") {
          // TODO: switch to a PostHog exception-capture helper once
          // lib/analytics.ts grows one.
          console.error("[firebase] validateAndFetch failed:", error, {
            seedTestId,
          });
        }
      }
    };

    validateAndFetch();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      changeFireBaseConnected?.(false);
      setIsConnectionPending(false);
    };
  }, [seedTestId, shouldStart, collectionName, uid]);

  return {
    data,
    isConnectionPending,
    notFound,
  };
};

export const useFirebaseDataForMultipleDocIds = (
  jobIds?: string[],
  collectionName?: string,
  shouldStart?: boolean,
  changeFireBaseConnected?: (isActive: boolean) => void,
) => {
  const [data, setData] = useState<FirebaseData[]>([]);
  const [completedIds, setCompletedIds] = useState<string[]>([]);
  const [isConnectionEstablished, setIsConnectionEstablished] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Clear data only when explicitly disabled or invalid params (but not for empty array)
    if (!shouldStart || !jobIds || !collectionName) {
      setData([]);
      setCompletedIds([]);
      setIsConnectionEstablished(false);
      return;
    }

    // If jobIds is empty array, don't set up listeners but keep existing data
    if (jobIds.length === 0) {
      setIsConnectionEstablished(false);
      return;
    }

    setIsConnectionEstablished(true);
    // Guards against the component (or this effect's deps) having changed
    // before `ensureFirebaseAuthReady()` resolves — without it, a fast
    // unmount/rerender could open a listener that immediately leaks, since
    // the listener setup below only starts after that await.
    let cancelled = false;
    const unsubscribers: Array<() => void> = [];

    const setupListeners = async () => {
      // Sign in to Firebase as this user (or await the in-flight sign-in)
      // before any Firestore call — the rules scope reads to the uid.
      const firebaseUid = await ensureFirebaseAuthReady();
      if (cancelled) return;
      if (!firebaseUid) {
        setIsConnectionEstablished(false);
        return;
      }

      try {
        // OPTION 1: Use single query with 'in' operator for ≤10 documents
        if (jobIds.length <= 10) {
          // The `array-contains` filter is required, not an optimisation: the
          // rules only allow a query that can return nothing but this user's
          // docs. A deleted (completed) doc simply drops out of the result.
          const q = query(
            collection(db, collectionName),
            where("accessibleUserIds", "array-contains", firebaseUid),
            where(documentId(), "in", jobIds),
          );

          const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
              changeFireBaseConnected?.(true);
              const newDocuments: FirebaseData[] = [];
              const fetchedDocIds = new Set<string>();
              querySnapshot.docs.forEach((docItem) => {
                const docData = convertTimestamps(docItem.data());
                newDocuments.push({
                  id: docItem.id,
                  ...(docData as object),
                } as FirebaseData);
                fetchedDocIds.add(docItem.id);
              });
              // A tracked id Firestore didn't return has completed (its doc
              // was removed) — drop just that entry and keep this listener
              // alive for the rest of the batch instead of tearing the whole
              // 'in' query down.
              const missingIds = jobIds.filter(
                (jobId) => !fetchedDocIds.has(jobId),
              );

              // Merge new data with existing data to persist previous jobIds
              setData((prevData) => {
                // Create a map of existing data by id for efficient lookup
                const existingMap = new Map(
                  prevData.map((item) => [item.id, item]),
                );
                // Update or add new documents
                newDocuments.forEach((docItem) => {
                  existingMap.set(docItem.id, docItem);
                });
                // Drop completed ids so a stale last-known value doesn't
                // linger after the doc is gone
                missingIds.forEach((missingId) => existingMap.delete(missingId));
                // Convert back to array
                return Array.from(existingMap.values());
              });

              if (missingIds.length > 0) {
                setCompletedIds((prev) =>
                  Array.from(new Set([...prev, ...missingIds])),
                );
              }

              setIsConnectionEstablished(true);
            },
            (error) => {
              setIsConnectionEstablished(false);
              changeFireBaseConnected?.(false);

              if (env.appEnv === "production") {
                // TODO: switch to a PostHog exception-capture helper once
                // lib/analytics.ts grows one.
                console.error(
                  "[firebase] query listener error (single-query):",
                  error,
                  {
                    jobIds,
                    collection: collectionName,
                    errorCode: error.code,
                  },
                );
              }
            },
          );

          unsubscribers.push(unsubscribe);
        }
        // OPTION 2: Use multiple individual listeners for >10 documents
        else {
          jobIds.forEach((jobId) => {
            const unsubscribe = onSnapshot(
              doc(db, collectionName, jobId),
              (docSnapshot) => {
                if (docSnapshot.exists()) {
                  const docData = convertTimestamps(docSnapshot.data());
                  const jobData = {
                    id: docSnapshot.id,
                    ...(docData as object),
                  } as FirebaseData;

                  // Merge new job data with existing data to persist previous jobIds
                  setData((prevData) => {
                    const existingIndex = prevData.findIndex(
                      (item) => item.id === jobId,
                    );
                    if (existingIndex >= 0) {
                      // Update existing item
                      const newData = [...prevData];
                      newData[existingIndex] = jobData;
                      return newData;
                    }
                    // Add new item
                    return [...prevData, jobData];
                  });
                  changeFireBaseConnected?.(true);
                } else {
                  // This job's doc is gone (complete) — drop just its entry
                  // and mark it completed; the other per-doc listeners in the
                  // batch keep running independently.
                  setData((prevData) =>
                    prevData.filter((item) => item.id !== jobId),
                  );
                  setCompletedIds((prev) =>
                    prev.includes(jobId) ? prev : [...prev, jobId],
                  );
                }
                setIsConnectionEstablished(true);
              },
              (error) => {
                if (isGoneError(error)) {
                  // Deleted (complete) — see `isGoneError`. Same as the
                  // `!exists()` branch above; the other listeners keep running.
                  setData((prevData) =>
                    prevData.filter((item) => item.id !== jobId),
                  );
                  setCompletedIds((prev) =>
                    prev.includes(jobId) ? prev : [...prev, jobId],
                  );
                  return;
                }
                setIsConnectionEstablished(false);

                if (env.appEnv === "production") {
                  // TODO: switch to a PostHog exception-capture helper once
                  // lib/analytics.ts grows one.
                  console.error(
                    "[firebase] query listener error (individual-listener):",
                    error,
                    {
                      jobId,
                      collection: collectionName,
                      errorCode: error.code,
                    },
                  );
                }
              },
            );

            unsubscribers.push(unsubscribe);
          });
        }
      } catch (error) {
        setIsConnectionEstablished(false);
        if (env.appEnv === "production") {
          // TODO: switch to a PostHog exception-capture helper once
          // lib/analytics.ts grows one.
          console.error("[firebase] setup error:", error, {
            jobIds,
            collection: collectionName,
            jobIdsCount: jobIds.length,
          });
        }
      }
    };

    void setupListeners();

    // Cleanup all listeners
    const cleanup = () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub());
      changeFireBaseConnected?.(false);
      setIsConnectionEstablished(false);
    };

    cleanupRef.current = cleanup;
    return cleanup;
  }, [jobIds?.join(","), collectionName, shouldStart]);

  return {
    data,
    completedIds,
    isConnectionEstablished,
    cleanup: () => cleanupRef.current?.(),
  };
};
